import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '../../components/Layout';
import { api } from '../../api/client';

interface Enrolment {
  id: string;
  candidate_name: string;
  candidate_email: string;
  trainer_name: string;
  programme_type: string;
  package: string;
  status: string;
  current_phase: string;
  current_session: number;
  start_date: string;
  created_at: string;
}

interface Trainer {
  id: string;
  name: string;
  email: string;
  is_active: number;
  capacity: number;
}

export function AdminEnrolments() {
  const [enrolments, setEnrolments] = useState<Enrolment[]>([]);
  const [trainers, setTrainers] = useState<Trainer[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ candidate_id: '', trainer_id: '', programme_type: 'lifespark360', package: 'branch', start_date: '' });
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get<Enrolment[]>('/admin/enrolments'),
      api.get<Trainer[]>('/admin/trainers'),
    ])
      .then(([e, t]) => { setEnrolments(e); setTrainers(t); })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreating(true);
    try {
      await api.post('/programmes/enrol', form);
      const updated = await api.get<Enrolment[]>('/admin/enrolments');
      setEnrolments(updated);
      setShowCreate(false);
    } catch (err) {
      console.error(err);
    }
    setCreating(false);
  };

  const statusColors: Record<string, string> = {
    active: 'bg-green-100 text-green-700',
    completed: 'bg-blue-100 text-blue-700',
    paused: 'bg-amber-100 text-amber-700',
    cancelled: 'bg-red-100 text-red-600',
    pending: 'bg-gray-100 text-gray-600',
  };

  const packageOptions: Record<string, string[]> = {
    lifespark360: ['seed', 'branch', 'canopy'],
    seedbed: ['standard', 'continuity'],
  };

  return (
    <Layout>
      <div className="max-w-6xl mx-auto mt-10 px-6 pb-20">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="font-display text-3xl font-bold text-mustard-green-dark">Enrolments</h1>
            <p className="text-mustard-charcoal/60 mt-1">{enrolments.length} total enrolments</p>
          </div>
          <button onClick={() => setShowCreate(!showCreate)} className="px-4 py-2 bg-mustard-green-dark text-white rounded-lg text-sm font-medium hover:bg-mustard-green transition-colors">
            {showCreate ? 'Cancel' : 'New Enrolment'}
          </button>
        </div>

        {/* Create Enrolment Form */}
        {showCreate && (
          <form onSubmit={handleCreate} className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 mb-6">
            <h2 className="font-display font-bold text-mustard-green-dark mb-4">Create Enrolment</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <input type="text" placeholder="Candidate ID" value={form.candidate_id} onChange={(e) => setForm({ ...form, candidate_id: e.target.value })} className="px-4 py-2 border rounded-lg text-sm" required />
              <select value={form.trainer_id} onChange={(e) => setForm({ ...form, trainer_id: e.target.value })} className="px-4 py-2 border rounded-lg text-sm" required>
                <option value="">Select Trainer</option>
                {trainers.filter((t) => t.is_active).map((t) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
              <select value={form.programme_type} onChange={(e) => setForm({ ...form, programme_type: e.target.value, package: packageOptions[e.target.value][0] })} className="px-4 py-2 border rounded-lg text-sm">
                <option value="lifespark360">LifeSpark360</option>
                <option value="seedbed">Seedbed</option>
              </select>
              <select value={form.package} onChange={(e) => setForm({ ...form, package: e.target.value })} className="px-4 py-2 border rounded-lg text-sm">
                {packageOptions[form.programme_type].map((p) => (
                  <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>
                ))}
              </select>
              <input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="px-4 py-2 border rounded-lg text-sm" required />
            </div>
            <button type="submit" disabled={creating} className="mt-4 px-6 py-2 bg-mustard-green-dark text-white rounded-lg text-sm font-medium disabled:opacity-50">
              {creating ? 'Creating...' : 'Create Enrolment'}
            </button>
          </form>
        )}

        {/* Enrolments Table */}
        {loading ? (
          <p className="text-mustard-charcoal/60">Loading...</p>
        ) : (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left px-5 py-3 text-xs font-medium text-mustard-charcoal/50 uppercase tracking-wider">Candidate</th>
                  <th className="text-left px-5 py-3 text-xs font-medium text-mustard-charcoal/50 uppercase tracking-wider">Trainer</th>
                  <th className="text-left px-5 py-3 text-xs font-medium text-mustard-charcoal/50 uppercase tracking-wider">Programme</th>
                  <th className="text-left px-5 py-3 text-xs font-medium text-mustard-charcoal/50 uppercase tracking-wider">Phase</th>
                  <th className="text-left px-5 py-3 text-xs font-medium text-mustard-charcoal/50 uppercase tracking-wider">Progress</th>
                  <th className="text-left px-5 py-3 text-xs font-medium text-mustard-charcoal/50 uppercase tracking-wider">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {enrolments.map((e) => {
                  const total = e.programme_type === 'lifespark360' ? 12 : 4;
                  return (
                    <tr key={e.id} className="hover:bg-gray-50">
                      <td className="px-5 py-4">
                        <p className="text-sm font-medium text-mustard-charcoal">{e.candidate_name}</p>
                        <p className="text-xs text-mustard-charcoal/40">{e.candidate_email}</p>
                      </td>
                      <td className="px-5 py-4 text-sm text-mustard-charcoal/60">{e.trainer_name}</td>
                      <td className="px-5 py-4">
                        <span className="text-xs font-medium">{e.programme_type === 'lifespark360' ? 'LS360' : 'Seedbed'}</span>
                        <span className="text-xs text-mustard-charcoal/40 ml-1">({e.package})</span>
                      </td>
                      <td className="px-5 py-4 text-sm font-medium text-mustard-charcoal">{e.current_phase}</td>
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-2">
                          <div className="w-16 bg-gray-200 rounded-full h-1.5">
                            <div className="bg-mustard-green-dark h-1.5 rounded-full" style={{ width: `${(e.current_session / total) * 100}%` }} />
                          </div>
                          <span className="text-xs text-mustard-charcoal/50">{e.current_session}/{total}</span>
                        </div>
                      </td>
                      <td className="px-5 py-4">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${statusColors[e.status] || 'bg-gray-100'}`}>
                          {e.status}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Layout>
  );
}
